import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { resolvePublicUrl } from '../../utils/publicUrl';
/** WV "Speed to Build" logo (same asset as the hero). */
const STB_LOGO = '/images/WestVirginia/onestophero/herologo.png';
/* ---- Motion ---- */
// Moving gradient accent line along the top edge (reads as an "active/AI" surface).
const shimmer = keyframes `
  0%   { background-position: 0% 0; }
  100% { background-position: 200% 0; }
`;
// Pulsing glow ring on the primary button.
const glowPulse = keyframes `
  0%, 100% { box-shadow: 0 8px 22px rgba(182,137,47,0.5), 0 0 0 0 rgba(182,137,47,0.5); }
  50%      { box-shadow: 0 12px 32px rgba(182,137,47,0.65), 0 0 0 12px rgba(182,137,47,0); }
`;
// Chevrons pulse in sequence → "fast-forward" speed cue.
const chevronPulse = keyframes `
  0%, 100% { opacity: 0.25; }
  50%      { opacity: 1; }
`;
// Twinkling AI sparkle.
const twinkle = keyframes `
  0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
  50%      { opacity: 0.55; transform: scale(1.18) rotate(16deg); }
`;
/**
 * Full-bleed "AI Advisor" CTA band between the hero and Departments. Animated AI orb + moving top-accent line
 * + speed streaks convey a fast, AI-powered experience; the large APPLY NOW button opens the AI Advisor.
 */
export default function SpeedToBuildCtaBand({ onApply }) {
    const theme = useTheme();
    const gold = theme.palette.secondary.main;
    const goldLight = theme.palette.secondary.light;
    const navyDark = theme.palette.primary.dark;
    const navy = theme.palette.primary.main;
    const logo = resolvePublicUrl(STB_LOGO) ?? STB_LOGO;
    return (
    // Full-bleed: no page gutters, spans the viewport width.
    _jsx(Box, { component: "section", sx: { width: '100%', mt: { xs: 1.5, md: 2 }, mb: { xs: 1.5, md: 2 } }, children: _jsxs(Box, { sx: {
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                background: 'linear-gradient(180deg, #ffffff 0%, #f4f7fc 100%)',
                borderTop: `1px solid ${alpha(navy, 0.08)}`,
                borderBottom: `1px solid ${alpha(navy, 0.08)}`,
            }, children: [_jsx(Box, { "aria-hidden": true, sx: {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        zIndex: 2,
                        background: `linear-gradient(90deg, ${gold}, ${navy}, ${goldLight}, ${gold})`,
                        backgroundSize: '200% 100%',
                        animation: `${shimmer} 6s linear infinite`,
                    } }), ['left', 'right'].map((side) => {
                    const isLeft = side === 'left';
                    return (_jsxs(Box, { "aria-hidden": true, sx: {
                            position: 'absolute',
                            [side]: { lg: 28, xl: 72 },
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 0,
                            display: { xs: 'none', lg: 'flex' },
                            flexDirection: isLeft ? 'row' : 'row-reverse',
                            alignItems: 'center',
                            gap: 1.5,
                            pointerEvents: 'none',
                        }, children: [_jsx(AutoAwesomeRoundedIcon, { sx: { color: gold, fontSize: 36, animation: `${twinkle} 2.4s ease-in-out infinite` } }), _jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [0, 1, 2, 3, 4].map((i) => (_jsx(Box, { sx: {
                                        color: alpha(navy, 0.85),
                                        lineHeight: 0,
                                        animation: `${chevronPulse} 1.4s ease-in-out infinite`,
                                        /* Delay runs outward from the action, so the pulse reads as motion toward APPLY NOW. */
                                        animationDelay: `${(isLeft ? i : 4 - i) * 0.12}s`,
                                    }, children: _jsx("svg", { width: "22", height: "44", viewBox: "0 0 22 44", fill: "none", style: { display: 'block' }, children: _jsx("path", { d: isLeft ? 'M6 7 L16 22 L6 37' : 'M16 7 L6 22 L16 37', stroke: "currentColor", strokeWidth: "4", strokeLinecap: "round", strokeLinejoin: "round" }) }) }, i))) })] }, side));
                }), _jsx(Box, { sx: {
                        position: 'relative',
                        zIndex: 1,
                        maxWidth: 960,
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: { xs: 1.25, md: 1.5 },
                        px: { xs: 2.5, md: 5 },
                        py: { xs: 2, md: 2.25 },
                    }, children: _jsxs(Box, { sx: { maxWidth: '100%', minWidth: 0, textAlign: 'center' }, children: [_jsx(Box, { sx: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.6,
                                    px: 1.25,
                                    py: 0.3,
                                    mb: 0.6,
                                    borderRadius: 999,
                                    bgcolor: alpha(gold, 0.12),
                                    border: `1px solid ${alpha(gold, 0.5)}`,
                                    color: navyDark,
                                    fontWeight: 800,
                                    fontSize: { xs: '0.58rem', md: '0.64rem' },
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                }, children: "AI-Powered Assistant" }), _jsxs(Typography, { component: "h2", sx: { m: 0, color: navyDark, fontWeight: 800, lineHeight: 1.1, fontSize: { xs: '1.2rem', md: '1.6rem' } }, children: [_jsx(Box, { component: "img", src: logo, alt: "", "aria-hidden": true, loading: "lazy", decoding: "async", sx: {
                                            height: '2.4em',
                                            width: 'auto',
                                            mr: '0.4em',
                                            verticalAlign: 'middle',
                                            display: 'inline-block',
                                        } }), "Apply for a", ' ', _jsx(Box, { component: "span", sx: { color: gold }, children: "Permit," }), ' ', _jsx(Box, { component: "span", sx: { color: gold }, children: "License," }), ' ', "or", ' ', _jsx(Box, { component: "span", sx: { color: gold }, children: "Certification" }), ' ', "in Minutes"] }), _jsx(Typography, { sx: { m: 0, mt: 0.25, color: 'text.secondary', fontSize: { xs: '0.8rem', md: '0.9rem' }, lineHeight: 1.4 }, children: "Let our AI Assistant guide you step-by-step." }), _jsxs(Box, { component: "button", type: "button", onClick: onApply, ...(onApply ? {} : { 'data-wv-chat': 'open', 'data-wv-chat-mode': 'docked' }), sx: {
                                    flexShrink: 0,
                                    mt: { xs: 1.25, md: 1.5 },
                                    cursor: 'pointer',
                                    border: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: { xs: 3.25, md: 4.25 },
                                    py: { xs: 1.15, md: 1.45 },
                                    borderRadius: 999,
                                    background: `linear-gradient(180deg, ${goldLight} 0%, ${gold} 100%)`,
                                    color: navyDark,
                                    fontWeight: 900,
                                    letterSpacing: '0.05em',
                                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                                    whiteSpace: 'nowrap',
                                    animation: `${glowPulse} 2.6s ease-in-out infinite`,
                                    transition: 'transform 0.15s ease',
                                    '&:hover': { transform: 'translateY(-2px) scale(1.02)' },
                                    '&:active': { transform: 'translateY(0)' },
                                }, children: [_jsx(AutoAwesomeRoundedIcon, { sx: { fontSize: { xs: 17, md: 20 } } }), "APPLY NOW", _jsx(ArrowForwardIosRoundedIcon, { sx: { fontSize: { xs: 14, md: 16 } } })] })] }) })] }) }));
}
